import { Checkbox, DefaultButton, Dialog, DialogFooter, Dropdown, IDropdownOption, Icon, PrimaryButton, Stack, Text, useTheme } from '@fluentui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Activity,
    ProjectDto,
    SetMultipleTimeEntryApprovalStateDto,
    SetTimeEntryApprovalActivityDto,
    TimeEntryApprovalDto,
} from '../../../../api/generated/data-contracts';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../api/api';
import { CUSTOM_RATE_ID } from '../Grid/CONSTANTS';
import { useApprovalStore } from '../../../../_grid/grid3';
import shallow from 'zustand/shallow';
import { useTimeUIConfig } from '../../../../hooks/queries/useTimeUIConfig';
import produce from 'immer';

export const SelectionBar = () => {
    const { selectedIds, setSelectedIds, approvals, projects, setApprovals } = useApprovalStore(
        store => ({
            selectedIds: store.selectedRows,
            setSelectedIds: store.setSelectedRows,
            approvals: store.approvals,
            setApprovals: store.setApprovals,
            projects: store.projects,
        }),
        shallow,
    );
    const [selectedActivity, setSelectedActivity] = useState<IDropdownOption<Activity> | null>(null);

    const approvalRows = useMemo(() => Object.values(approvals), [approvals]);

    const selectedApprovalRowStates = useMemo(() => approvalRows.filter(approvalRow => selectedIds.has(approvalRow.id)), [approvalRows, selectedIds]);

    useEffect(() => {
        if (!selectedIds.size) {
            setSelectedActivity(null);
        }
    }, [selectedIds]);

    const checkBoxState = useMemo(() => {
        if (!selectedIds.size) {
            return {
                indeterminate: false,
                checked: false,
            };
        }
        if (selectedIds.size === approvalRows.length) {
            return {
                indeterminate: false,
                checked: true,
            };
        }
        return {
            indeterminate: true,
            checked: false,
        };
    }, [approvalRows.length, selectedIds.size]);

    const theme = useTheme();
    const { activities } = useApprovalStore(store => ({ activities: store.activities }), shallow);

    const options = useMemo(
        (): IDropdownOption<Activity>[] =>
            Object.values(activities)
                .filter(act => act.id !== CUSTOM_RATE_ID)
                .map(activity => {
                    return {
                        key: activity.id,
                        text: activity.name,
                        data: activity,
                    };
                }),
        [activities],
    );

    const { mutate: mutateActivities } = useMutation((approvals: TimeEntryApprovalDto[]) =>
        ApiCalls.setMultipleActivity({
            approvals: approvals.map((approval): SetTimeEntryApprovalActivityDto => {
                return {
                    selectedActivity: activities[approval.activityId],
                    timeEntryId: approval.timeEntry.id,
                    approvalId: approval.id,
                };
            }),
        }),
    );

    const { mutate: mutateApprovalStates } = useMutation((approvals: SetMultipleTimeEntryApprovalStateDto) => ApiCalls.setMultipleApprovalState(approvals));

    const [bulkApprovals, setBulkApprovals] = useState<BulkApprovals | null>(null);

    const bulkApprove = useCallback(async () => {
        const finishedProjects = getFinishedProjectsFromSelection(approvalRows, selectedApprovalRowStates, Object.values(projects));
        if (finishedProjects.length) {
            setBulkApprovals({ finishedProjects, selectedRowStates: selectedApprovalRowStates });
        } else {
            setApprovals(
                produce(approvals => {
                    selectedIds.forEach(id => {
                        approvals[id].approvalState = 'Approved';
                    });
                }),
            );

            mutateApprovalStates({
                approvals: selectedApprovalRowStates.map(approvalRow => ({
                    timeEntryId: approvalRow.timeEntry.id,
                    selectedApprovalState: 'Approved',
                    approvalId: approvalRow.id,
                })),
            });
        }
    }, [approvalRows, mutateApprovalStates, projects, selectedApprovalRowStates, selectedIds, setApprovals]);

    const onChangeActivity = useCallback(
        async (e: any, opt: IDropdownOption<Activity>) => {
            if (opt?.data) {
                setSelectedActivity(opt);
                const nonApprovedApprovals = selectedApprovalRowStates
                    .filter(row => approvals[row.id].approvalState !== 'Approved')
                    .map(approval => ({
                        ...approval,
                        activityId: opt.data.id,
                    }));
                setApprovals(
                    produce(approvals => {
                        nonApprovedApprovals.forEach(({ id, activityId }) => {
                            approvals[id].activityId = activityId;
                        });
                    }),
                );
                setSelectedIds(new Set());
                mutateActivities(nonApprovedApprovals);
            }
        },
        [approvals, mutateActivities, selectedApprovalRowStates, setApprovals, setSelectedIds],
    );

    const { config } = useTimeUIConfig();

    return (
        <>
            <Stack
                horizontal
                horizontalAlign="space-between"
                styles={{ root: { padding: '0 8px', border: `1px solid ${theme.semanticColors.disabledBackground}`, height: 60 } }}
            >
                <Stack horizontal verticalAlign="center">
                    <Checkbox
                        {...checkBoxState}
                        onChange={(e, val) => {
                            if (val) {
                                setSelectedIds(new Set(approvalRows.map(approval => approval.id)));
                            } else {
                                setSelectedIds(new Set());
                            }
                        }}
                    />
                    <Text>Select all</Text>
                    {selectedIds.size !== 0 && (
                        <>
                            <div
                                style={{
                                    display: 'inline-block',
                                    height: '1rem',
                                    width: 1,
                                    backgroundColor: theme.semanticColors.bodyText,
                                    marginLeft: 10,
                                    marginRight: 10,
                                }}
                            />
                            <Text>{selectedIds.size} Selected</Text>
                        </>
                    )}
                </Stack>
                <Stack horizontal verticalAlign="center" styles={{ root: { height: '100%', gap: 20 } }}>
                    {selectedIds.size !== 0 && (
                        <>
                            {Boolean(config.approval.projectManagerApproval.useRates) && (
                                <Dropdown
                                    options={options}
                                    styles={{ root: { width: 200 } }}
                                    placeholder="Bulk edit activity"
                                    onChange={onChangeActivity}
                                    selectedKey={selectedActivity?.key as string}
                                />
                            )}
                            <PrimaryButton styles={{ root: { backgroundColor: theme.semanticColors.successIcon } }} onClick={bulkApprove}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                                    <Text>Approve</Text>
                                    <Icon iconName="CheckMark" />
                                </Stack>
                            </PrimaryButton>
                        </>
                    )}
                </Stack>
            </Stack>
            {bulkApprovals && (
                <BulkApprovalDialog
                    bulkApprovals={bulkApprovals}
                    onApprove={() => {
                        const { selectedRowStates } = bulkApprovals;
                        setApprovals(
                            produce(approvals => {
                                selectedRowStates.forEach(row => {
                                    approvals[row.id].approvalState = 'Approved';
                                });
                            }),
                        );
                        mutateApprovalStates({
                            approvals: selectedRowStates.map(appr => ({
                                selectedApprovalState: 'Approved',
                                timeEntryId: appr.timeEntry.id,
                                approvalId: appr.id,
                            })),
                        });
                        setBulkApprovals(null);
                    }}
                    onDismiss={() => setBulkApprovals(null)}
                />
            )}
        </>
    );
};

const getFinishedProjectsFromSelection = (allApprovals: TimeEntryApprovalDto[], selectedApprovals: TimeEntryApprovalDto[], projects: ProjectDto[]) => {
    return projects.filter(project => {
        const missingApprovalsForProject = allApprovals.filter(approval => approval.projectId === project.id && approval.approvalState !== 'Approved');
        return (
            missingApprovalsForProject.length &&
            missingApprovalsForProject.every(missingApproval => selectedApprovals.find(selectedApproval => selectedApproval.id === missingApproval.id))
        );
    });
};

type BulkApprovals = { selectedRowStates: TimeEntryApprovalDto[]; finishedProjects: ProjectDto[] };

const BulkApprovalDialog = ({
    bulkApprovals: { finishedProjects },
    onApprove,
    onDismiss,
}: {
    bulkApprovals: BulkApprovals;
    onDismiss: () => void;
    onApprove: () => void;
}) => {
    const theme = useTheme();
    return (
        <Dialog onDismiss={onDismiss} hidden={false} dialogContentProps={{ title: 'Approve and close time registration?' }} minWidth={500}>
            <Stack tokens={{ childrenGap: 10 }}>
                <Text>
                    After approving all hours, time registration will be closed for the given time period and restricted from further changes on the following
                    projects:
                </Text>
                <ul>
                    {finishedProjects.map(project => {
                        return (
                            <li key={project.id}>
                                <Text>{project.name}</Text>
                            </li>
                        );
                    })}
                </ul>
            </Stack>

            <DialogFooter>
                <DefaultButton onClick={onDismiss}>Cancel</DefaultButton>
                <PrimaryButton onClick={onApprove}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <Text styles={{ root: { color: theme.semanticColors.accentButtonText } }}>Approve</Text>
                        <Icon iconName="CheckMark" />
                    </Stack>
                </PrimaryButton>
            </DialogFooter>
        </Dialog>
    );
};
